import { Stack, Typography } from '@mui/material';
import { UserCommunicationPreference } from '@provision/entity-types';
import { getFullName } from '@provision/formatting-utils';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { AvatarUploader } from 'src/components/CreateUserModal/avatar-uploader';
import {
  AvatarFlushFailureReason,
  AvatarFlushStatus,
  useAvatarSelection,
} from 'src/components/CreateUserModal/use-avatar-selection';
import { getUserModalActions } from 'src/components/CreateUserModal/user-modal-actions';
import {
  FormAutocomplete,
  FormPhoneInput,
  FormTextField,
  MAX_NUMBER_LENGTH,
  MIN_NUMBER_LENGTH,
} from 'src/components/Forms';
import {
  appendCodeToPhoneNumber,
  removeCodeFromPhoneNumber,
} from 'src/components/Forms/utils';
import { GenericForm } from 'src/components/GenericForm/generic-form';
import { ModalContainer } from 'src/components/ModalContainer';
import { PlatformUser } from 'src/graphql';
import {
  getPlatformUserAvatarStorageKey,
  getPlatformUserAvatarUrl,
} from 'src/graphql/entities/platform-user/platform-user-avatar-url';
import { useDeletePlatformUserAvatar } from 'src/graphql/entities/platform-user/use-delete-platform-user-avatar';
import {
  SignalNotificationOption,
  useManageUserNotificationPreferences,
  useSignalNotificationsList,
} from 'src/graphql/entities/user-communication-preferences';
import { Modals, useModalMethods, useModalState } from 'src/store/modals';
import { useNotifications } from 'src/utils/hooks/use-notifications';
import { UpdateUserFormFields, UpdateUserFormModel } from './types';

const NOTIFICATION_FIELDS = [
  {
    name: UpdateUserFormFields.PrimaryNotificationChannel,
    label: '1st Notification',
    testId: 'update-user-notification-primary',
  },
  {
    name: UpdateUserFormFields.SecondaryNotificationChannel,
    label: '2nd Notification',
    testId: 'update-user-notification-secondary',
  },
  {
    name: UpdateUserFormFields.TertiaryNotificationChannel,
    label: '3rd Notification',
    testId: 'update-user-notification-tertiary',
  },
];

const notificationFormFields = NOTIFICATION_FIELDS.map(({ name }) => name);

const NOTIFICATION_STEP_PRIORITY = 10;
const MAX_NOTIFICATION_PRIORITY = 30;

export const UpdateUserModal = () => {
  const defaultSignalOptions = useSignalNotificationsList();
  const { closeModal } = useModalMethods(Modals.UpdateUser);
  const { isOpened, modalProps } = useModalState(Modals.UpdateUser);

  const { disabledFields = [] } = modalProps || {};

  const selectedPreferences = useSelectedNotificationOptions(
    defaultSignalOptions,
    modalProps?.user.userCommunicationPreferences,
  );

  const { canManageNotificationPreferences } = useManageUserNotificationPreferences();

  const notificationPreferencesAreVisible = useMemo(
    () => canManageNotificationPreferences(modalProps?.user),
    [modalProps?.user, canManageNotificationPreferences],
  );

  const formMethods = useForm<UpdateUserFormModel>({
    mode: 'all',
    values: {
      [UpdateUserFormFields.Id]: modalProps?.user?.id ?? '',
      [UpdateUserFormFields.FirstName]: modalProps?.user?.firstName ?? '',
      [UpdateUserFormFields.LastName]: modalProps?.user?.lastName ?? '',
      [UpdateUserFormFields.Email]: modalProps?.user?.email ?? '',
      [UpdateUserFormFields.PhoneNumber]: removeCodeFromPhoneNumber(
        modalProps?.user?.phoneNumber ?? '',
      ),
      [UpdateUserFormFields.AvatarFileKey]: modalProps?.user?.avatarFileKey ?? null,
      [UpdateUserFormFields.PrimaryNotificationChannel]: selectedPreferences[0] || null,
      [UpdateUserFormFields.SecondaryNotificationChannel]: selectedPreferences[1] || null,
      [UpdateUserFormFields.TertiaryNotificationChannel]: selectedPreferences[2] || null,
    },
  });

  const { formState, reset, handleSubmit, watch, setValue, getValues } = formMethods;

  const avatar = useAvatarSelection();

  const phoneNumber = watch(UpdateUserFormFields.PhoneNumber);

  const availableSignalOptions = useMemo(
    () =>
      phoneNumber
        ? defaultSignalOptions
        : defaultSignalOptions.filter((item) => !item.requirePhone),
    [phoneNumber, defaultSignalOptions],
  );

  useEffect(() => {
    /**
     * if phone number is not set we allow to select email option only and
     * clean all other options if they have phone call or sms selected
     */
    const phoneNumberIsNotSet = !phoneNumber && !getValues()[UpdateUserFormFields.PhoneNumber];
    if (phoneNumberIsNotSet) {
      const formData = getValues();
      const optionsWithPhoneNotRequired = !phoneNumber
        ? defaultSignalOptions.filter((item) => !item.requirePhone)
        : defaultSignalOptions;

      notificationFormFields.forEach((item) => {
        const formValue = formData[item] as SignalNotificationOption;
        const isValueValid = optionsWithPhoneNotRequired.find(
          (filterOptionItem) => filterOptionItem.value === formValue?.value,
        );

        if (!isValueValid) {
          setValue(item, null);
        }
      });
    }
  }, [phoneNumber, getValues, defaultSignalOptions, selectedPreferences, setValue]);

  const closeModalAndResetForm = useCallback(() => {
    reset();
    avatar.reset();
    closeModal();
  }, [reset, avatar, closeModal]);

  const { deleteAvatar } = useDeletePlatformUserAvatar();
  const { showErrorAlert } = useNotifications();

  const onFormSubmit = useCallback(
    async (formValues: UpdateUserFormModel) => {
      const flush = await avatar.flush(modalProps?.user?.partnerId);
      if (flush.status === AvatarFlushStatus.Failed) {
        if (flush.reason === AvatarFlushFailureReason.MissingPartner) {
          showErrorAlert('Cannot upload the photo: missing partner. Please reopen the form.');
        }
        return;
      }
      const avatarFileKey =
        flush.status === AvatarFlushStatus.Uploaded
          ? flush.fileKey
          : (formValues[UpdateUserFormFields.AvatarFileKey] ?? undefined);

      // phone numbers for platform users should be stored with a code
      const preparedFormValues: Partial<PlatformUser> = {
        [UpdateUserFormFields.Id]: formValues[UpdateUserFormFields.Id],
        [UpdateUserFormFields.FirstName]: formValues[UpdateUserFormFields.FirstName].trim(),
        [UpdateUserFormFields.LastName]: formValues[UpdateUserFormFields.LastName].trim(),
        [UpdateUserFormFields.PhoneNumber]: appendCodeToPhoneNumber(
          formValues[UpdateUserFormFields.PhoneNumber],
        ),
        [UpdateUserFormFields.AvatarFileKey]: avatarFileKey ?? undefined,
        userCommunicationPreferences: userNotificationOptionsToUserPreference(
          formValues,
          modalProps?.user.id!,
        ),
      };

      if (!formState.isDirty && flush.status !== AvatarFlushStatus.Uploaded) {
        closeModalAndResetForm();
        return;
      }
      try {
        await modalProps?.onSubmit({
          ...modalProps.user,
          ...preparedFormValues,
        });
        closeModalAndResetForm();
      } catch {
        if (flush.status === AvatarFlushStatus.Uploaded) {
          void deleteAvatar(
            getPlatformUserAvatarStorageKey(modalProps?.user?.partnerId, flush.fileKey),
          );
        }
      }
    },
    [
      avatar,
      modalProps,
      closeModalAndResetForm,
      formState.isDirty,
      deleteAvatar,
      showErrorAlert,
    ],
  );

  return (
    <ModalContainer
      open={isOpened}
      size="medium"
      title="Update user"
      actions={getUserModalActions({
        submitLabel: 'Save changes',
        loadingLabel: 'Saving…',
        onSubmit: handleSubmit(onFormSubmit),
        isSubmitting: formState.isSubmitting,
        isFormValid: formState.isValid,
      })}
      onClose={closeModalAndResetForm}
      data-testid="update-user-modal"
    >
      <AvatarUploader
        name={getFullName(modalProps?.user)}
        imageUrl={
          avatar.previewUrl ??
          getPlatformUserAvatarUrl(
            modalProps?.user?.partnerId,
            modalProps?.user?.avatarFileKey,
          )
        }
        onFileChange={avatar.onFileChange}
      />
      <GenericForm<UpdateUserFormModel> formMethods={formMethods}>
        <FormTextField
          type="textfield"
          name={UpdateUserFormFields.FirstName}
          label="First name"
          required
          disabled={disabledFields.includes(UpdateUserFormFields.FirstName)}
          textFieldProps={{ inputProps: { 'data-testid': 'update-user-first-name' } }}
        />
        <FormTextField
          type="textfield"
          name={UpdateUserFormFields.LastName}
          label="Last name"
          required
          disabled={disabledFields.includes(UpdateUserFormFields.LastName)}
          textFieldProps={{ inputProps: { 'data-testid': 'update-user-last-name' } }}
        />
        <FormTextField
          type="textfield"
          name={UpdateUserFormFields.Email}
          label="Email"
          disabled
          textFieldProps={{ inputProps: { 'data-testid': 'update-user-email' } }}
        />
        <FormPhoneInput
          type="phone"
          name={UpdateUserFormFields.PhoneNumber}
          label="Phone"
          minLength={MIN_NUMBER_LENGTH}
          maxLength={MAX_NUMBER_LENGTH}
          textFieldProps={{
            disabled: disabledFields.includes(UpdateUserFormFields.PhoneNumber),
            inputProps: { 'data-testid': 'update-user-phone' },
          }}
        />
        {notificationPreferencesAreVisible ? (
          <Stack gap={2}>
            <Stack gap={0.5}>
              <Typography variant="subtitle2">
                ProVision Signal Notification Preferences
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Set the order in which you would like escalation notifications to be sent via
                ProVision Signal. Email is the default notification if no preferences are set.
              </Typography>
            </Stack>
            {NOTIFICATION_FIELDS.map(({ name, label, testId }) => (
              <FormAutocomplete
                key={name}
                type="autocomplete"
                name={name}
                label={label}
                data-testid={testId}
                options={availableSignalOptions}
                disabled={disabledFields.includes(name)}
              />
            ))}
          </Stack>
        ) : null}
      </GenericForm>
    </ModalContainer>
  );
};

function useSelectedNotificationOptions(
  defaultSignalOptions: SignalNotificationOption[],
  selectedPreferences?: UserCommunicationPreference[],
): (SignalNotificationOption | null)[] {
  return useMemo(() => {
    if (!selectedPreferences?.length) {
      return [];
    }

    return selectedPreferences.map((preference) => {
      const optionConfig = defaultSignalOptions.find(
        (item) => item.value === preference.communicationChannel,
      );

      return {
        value: preference.communicationChannel,
        id: preference.communicationChannel,
        label: optionConfig?.label ?? '',
        requirePhone: optionConfig?.requirePhone!,
      };
    });
  }, [defaultSignalOptions, selectedPreferences]);
}

function userNotificationOptionsToUserPreference(
  formData: UpdateUserFormModel,
  userId: string,
): UserCommunicationPreference[] {
  return notificationFormFields
    .map((item) => (formData[item] as SignalNotificationOption)?.value)
    .filter(Boolean)
    .map(
      (item, index) =>
        ({
          priority: MAX_NOTIFICATION_PRIORITY - NOTIFICATION_STEP_PRIORITY * index,
          communicationChannel: item,
          userId,
        }) as UserCommunicationPreference,
    );
}
